import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { submitLead } from "../lib/api";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, buildWhatsAppLink } from "../lib/constants";

const REQUIREMENTS = ["Logo", "Signage", "Social Media", "Branding", "Other"];

const initial = {
    name: "",
    phone: "",
    business_name: "",
    requirement: "",
    message: "",
};

export const LeadForm = ({ id = "consult" }) => {
    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const setField = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Please enter your name";
        const phoneDigits = form.phone.replace(/\D/g, "");
        if (phoneDigits.length < 7) e.phone = "Please enter a valid phone number";
        if (!form.requirement) e.requirement = "Select what you need";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            await submitLead({
                name: form.name.trim(),
                phone: form.phone.trim(),
                business_name: form.business_name.trim(),
                requirement: form.requirement,
                message: form.message.trim(),
            });

            toast.success("Request received!", {
                description: "Opening WhatsApp so we can chat right now.",
            });

            const text = `Hi Raj Graphics! 👋\n\nName: ${form.name}\nPhone: ${form.phone}\nBusiness: ${form.business_name || "—"}\nRequirement: ${form.requirement}${form.message ? `\nMessage: ${form.message}` : ""}\n\nI'd like to book a free strategy call.`;
            window.open(buildWhatsAppLink(text), "_blank", "noopener,noreferrer");

            setForm(initial);
        } catch (err) {
            toast.error("Couldn't send right now", {
                description: "Please try again or call us directly.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id={id}
            data-testid="lead-form-section"
            className="relative py-24 sm:py-32 bg-[#050505] border-t border-white/5 overflow-hidden"
        >
            <div className="absolute -top-32 -right-20 w-[500px] h-[500px] rounded-full bg-gold/[0.08] blur-[120px] pointer-events-none" />
            <div className="absolute inset-0 grid-pattern radial-fade opacity-50 pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-5"
                    >
                        <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-4">
                            Free Strategy Call
                        </div>
                        <h2 className="font-poppins font-semibold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">
                            Get Your Free <br />
                            <span className="gold-gradient-text">Strategy Call</span>
                        </h2>
                        <p className="mt-5 font-inter text-base leading-relaxed text-zinc-400 max-w-md">
                            Tell us about your business and what you need. We'll get back within
                            a few hours with a clear next step — and no pressure.
                        </p>

                        <div className="mt-10 space-y-4">
                            <a
                                href={`tel:${PHONE_TEL}`}
                                data-testid="lead-info-phone"
                                className="flex items-center gap-3 text-zinc-300 hover:text-gold transition-colors group"
                            >
                                <span className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-gold/40">
                                    <Phone size={16} className="text-gold" />
                                </span>
                                <span className="font-inter">{PHONE_DISPLAY}</span>
                            </a>
                            <a
                                href={`mailto:${EMAIL}`}
                                data-testid="lead-info-email"
                                className="flex items-center gap-3 text-zinc-300 hover:text-gold transition-colors group"
                            >
                                <span className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-gold/40">
                                    <Mail size={16} className="text-gold" />
                                </span>
                                <span className="font-inter">{EMAIL}</span>
                            </a>
                        </div>
                    </motion.div>

                    <motion.form
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        onSubmit={onSubmit}
                        noValidate
                        data-testid="lead-form"
                        className="lg:col-span-7 bg-white/[0.02] border border-white/10 rounded-2xl p-7 md:p-10 backdrop-blur-sm"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div className="md:col-span-1">
                                <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3 block">
                                    Your Name *
                                </label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setField("name", e.target.value)}
                                    data-testid="lead-input-name"
                                    autoComplete="name"
                                    className="bg-transparent border-b border-white/15 focus:border-gold text-white py-3 outline-none w-full transition-colors font-inter"
                                    placeholder="Rohan Sharma"
                                />
                                {errors.name && (
                                    <p className="mt-2 text-xs text-red-400 font-inter">{errors.name}</p>
                                )}
                            </div>

                            <div className="md:col-span-1">
                                <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3 block">
                                    Phone Number *
                                </label>
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={(e) => setField("phone", e.target.value)}
                                    data-testid="lead-input-phone"
                                    autoComplete="tel"
                                    className="bg-transparent border-b border-white/15 focus:border-gold text-white py-3 outline-none w-full transition-colors font-inter"
                                    placeholder="+91 98xxx xxxxx"
                                />
                                {errors.phone && (
                                    <p className="mt-2 text-xs text-red-400 font-inter">{errors.phone}</p>
                                )}
                            </div>

                            <div className="md:col-span-1">
                                <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3 block">
                                    Business Name
                                </label>
                                <input
                                    type="text"
                                    value={form.business_name}
                                    onChange={(e) => setField("business_name", e.target.value)}
                                    data-testid="lead-input-business"
                                    className="bg-transparent border-b border-white/15 focus:border-gold text-white py-3 outline-none w-full transition-colors font-inter"
                                    placeholder="Your brand or business"
                                />
                            </div>

                            <div className="md:col-span-1">
                                <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3 block">
                                    Your Requirement *
                                </label>
                                <Select
                                    value={form.requirement}
                                    onValueChange={(v) => setField("requirement", v)}
                                >
                                    <SelectTrigger
                                        data-testid="lead-select-requirement"
                                        className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-3 h-auto text-white hover:border-gold focus:border-gold focus:ring-0 font-inter data-[placeholder]:text-zinc-500"
                                    >
                                        <SelectValue placeholder="Select an option" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-950 border border-white/10 text-white">
                                        {REQUIREMENTS.map((r) => (
                                            <SelectItem
                                                key={r}
                                                value={r}
                                                data-testid={`lead-select-option-${r.toLowerCase().replace(/\s+/g, "-")}`}
                                                className="focus:bg-gold/10 focus:text-gold"
                                            >
                                                {r}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.requirement && (
                                    <p className="mt-2 text-xs text-red-400 font-inter">{errors.requirement}</p>
                                )}
                            </div>

                            <div className="md:col-span-2">
                                <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3 block">
                                    Tell us a bit more (optional)
                                </label>
                                <textarea
                                    value={form.message}
                                    onChange={(e) => setField("message", e.target.value)}
                                    data-testid="lead-input-message"
                                    rows={3}
                                    className="bg-transparent border-b border-white/15 focus:border-gold text-white py-3 outline-none w-full transition-colors font-inter resize-none"
                                    placeholder="Quick context about your project, deadline, or budget..."
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="lead-submit-btn"
                            className="mt-10 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold hover:bg-[#C5A030] disabled:opacity-60 disabled:cursor-not-allowed text-black font-medium font-inter px-8 py-3.5 rounded-full transition-all duration-200 hover:shadow-[0_10px_40px_rgba(212,175,55,0.35)]"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    Sending…
                                </>
                            ) : (
                                <>
                                    Book Free Consultation
                                    <ArrowRight size={18} />
                                </>
                            )}
                        </button>
                        <p className="mt-4 font-inter text-xs text-zinc-500">
                            By submitting, you agree to be contacted on the phone number provided.
                        </p>
                    </motion.form>
                </div>
            </div>
        </section>
    );
};

export default LeadForm;
